/**
 * Browser detection and compatibility utilities
 */

export const detectBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes("safari") && !userAgent.includes("chrome")) {
    return "safari";
  } else if (userAgent.includes("firefox")) {
    return "firefox";
  } else if (userAgent.includes("chrome")) {
    return "chrome";
  } else if (userAgent.includes("edge")) {
    return "edge";
  }

  return "unknown";
};

export const isSafari = () => detectBrowser() === "safari";
export const isFirefox = () => detectBrowser() === "firefox";
export const isChrome = () => detectBrowser() === "chrome";
export const isEdge = () => detectBrowser() === "edge";

/**
 * Get browser-specific fetch options for better compatibility
 */
export const getBrowserCompatibleFetchOptions = (options = {}) => {
  const browser = detectBrowser();

  const baseOptions = {
    credentials: "include",
    mode: "cors", // Explicitly set CORS mode for all browsers
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...options.headers,
    },
    cache: "no-cache",
    redirect: "follow", // Handle redirects properly
    ...options,
  };

  // Safari-specific adjustments
  if (browser === "safari") {
    // Safari is more strict about CORS and cookies
    baseOptions.referrerPolicy = "strict-origin-when-cross-origin";
  }

  // Firefox-specific adjustments
  if (browser === "firefox") {
    // Firefox handles some headers differently and has stricter third-party cookie policies
    baseOptions.headers["Cache-Control"] = "no-cache";
    baseOptions.headers["Pragma"] = "no-cache";
    // Firefox needs explicit referrer policy for cross-origin requests
    baseOptions.referrerPolicy = "strict-origin-when-cross-origin";
  }

  // Chrome/Edge adjustments
  if (browser === "chrome" || browser === "edge") {
    // Chrome and Edge are generally more permissive, but set explicit policies
    baseOptions.referrerPolicy = "strict-origin-when-cross-origin";
  }

  return baseOptions;
};

/**
 * Check if browser supports required features for authentication
 */
export const checkBrowserCompatibility = () => {
  const issues = [];
  const browser = detectBrowser();

  // Check for required features
  if (!window.fetch) {
    issues.push("Fetch API not supported");
  }

  if (!window.localStorage) {
    issues.push("LocalStorage not supported");
  }

  if (!window.sessionStorage) {
    issues.push("SessionStorage not supported");
  }

  // Check for third-party cookie support (important for cross-origin auth)
  if (isSafari() || isFirefox()) {
    // These browsers have stricter third-party cookie policies
    console.warn(
      "Browser has strict third-party cookie policies. Authentication may require user interaction."
    );
  }

  // Firefox-specific compatibility checks
  if (browser === "firefox") {
    // Check if we're in a secure context (required for some features)
    if (!window.isSecureContext) {
      issues.push("Firefox requires secure context for authentication");
    }

    // Check for third-party cookie blocking
    const hasThirdPartyCookieSupport = checkThirdPartyCookieSupport();
    if (!hasThirdPartyCookieSupport) {
      issues.push("Firefox may be blocking third-party cookies");
    }

    // Check for Stripe cookie issues
    const stripeCookieSupport = checkStripeCookieSupport();
    if (!stripeCookieSupport.supported) {
      issues.push("Stripe cookies may be blocked by Firefox privacy settings");
    }
  }

  return {
    compatible: issues.length === 0,
    issues,
    browser,
  };
};

/**
 * Check if third-party cookies are supported (Firefox-specific)
 */
const checkThirdPartyCookieSupport = () => {
  try {
    void document.cookie;
    return true;
  } catch (error) {
    console.warn("Third-party cookie support check failed:", error);
    return false;
  }
};

/**
 * Check if Stripe cookies are being blocked by the browser
 */
const checkStripeCookieSupport = () => {
  const browser = detectBrowser();

  // Firefox and Safari have stricter cookie policies
  if (browser === "firefox" || browser === "safari") {
    // Check if we can access Stripe-related cookies
    const cookies = document.cookie.split(";");
    const stripeCookies = cookies.filter(
      (cookie) =>
        cookie.trim().startsWith("__stripe_") ||
        cookie.trim().startsWith("stripe_")
    );

    return {
      supported: stripeCookies.length > 0,
      cookies: stripeCookies,
      browser,
    };
  }

  return {
    supported: true,
    cookies: [],
    browser,
  };
};

/**
 * Handle browser-specific authentication errors
 */
export const handleAuthError = (error, context = "authentication") => {
  const browser = detectBrowser();

  console.error(`${context} error in ${browser}:`, error);

  // Safari-specific error handling
  if (browser === "safari") {
    if (
      error.message?.includes("CORS") ||
      error.message?.includes("credentials")
    ) {
      return new Error(
        "Authentication failed due to browser security settings. Please ensure third-party cookies are enabled and try again."
      );
    }
  }

  // Firefox-specific error handling
  if (browser === "firefox") {
    if (
      error.message?.includes("NetworkError") ||
      error.message?.includes("TypeError") ||
      error.message?.includes("Failed to fetch")
    ) {
      return new Error(
        "Network error detected. This may be due to Firefox's strict privacy settings. Please check your connection and try again."
      );
    }

    // Handle CORS errors in Firefox
    if (
      error.message?.includes("CORS") ||
      error.message?.includes("credentials")
    ) {
      return new Error(
        "Authentication failed due to Firefox's privacy settings. Please check your browser's privacy and security settings and try again."
      );
    }

    // Handle cookie-related errors
    if (
      error.message?.includes("cookie") ||
      error.message?.includes("SameSite")
    ) {
      return new Error(
        "Cookie settings issue detected. Please check Firefox's cookie settings and ensure third-party cookies are allowed for this site."
      );
    }
  }

  return error;
};
